import { Component, EventEmitter, Input, OnInit, Output, Inject, PLATFORM_ID, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';

@Component({
  selector: 'app-order-countdown',
  templateUrl: './order-countdown.component.html',
  styleUrls: ['./order-countdown.component.scss']
})
export class OrderCountdownComponent implements OnInit, OnDestroy, OnChanges {
  @Input() expiryTime!: string; // Input: Server-provided expiry time
  @Input() serverTime!: string; // Input: Server-provided current time

  @Output() onShowExpiryDate = new EventEmitter<boolean>();
  @Output() onTimeup = new EventEmitter<boolean>();

  showTimer: boolean = false;
  isAlert: boolean = false;
  minutes: string = '00';
  seconds: string = '00';

  private initialRemainingTime!: number; // Total remaining time in ms at initialization
  private clientStartTime!: number; // Client time when the timer initialized
  private interval: any;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit() {
    this.initializeTimer();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['expiryTime'] || changes['serverTime']) {
      if (!changes['expiryTime']?.isFirstChange() || !changes['serverTime']?.isFirstChange()) {
        this.initializeTimer();
      }
    }
  }

  initializeTimer() {
    if (this.interval) {
      clearInterval(this.interval);
    }

    if (!this.expiryTime || !this.serverTime) return;

    // Use native Date to correctly parse mixed timezone formats (e.g. local vs UTC 'Z' strings)
    // exactly as the original code did
    const expiryTimestamp = new Date(this.expiryTime).getTime();
    const serverTimestamp = new Date(this.serverTime).getTime();

    // Calculate total remaining time in milliseconds based on the server's perspective
    this.initialRemainingTime = Math.max(expiryTimestamp - serverTimestamp, 0);
    
    // Capture the client time at initialization
    this.clientStartTime = new Date().getTime();

    if (this.initialRemainingTime <= 0) {
      this.minutes = '00';
      this.seconds = '00';
      this.onTimeup.emit(true);
      return;
    }

    this.startTimer();
  }

  startTimer() {
    if (isPlatformBrowser(this.platformId)) {
      this.interval = setInterval(() => {
        // Calculate the remaining time based on the actual elapsed time since initialization
        const now = new Date().getTime();
        const timeElapsedSinceClientStart = now - this.clientStartTime;

        const remainingTime = Math.max(this.initialRemainingTime - timeElapsedSinceClientStart, 0);

        if (remainingTime > 0) {
          const totalSeconds = Math.floor(remainingTime / 1000); // Convert ms to seconds
          this.minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
          this.seconds = (totalSeconds % 60).toString().padStart(2, '0');

          // Show only if <= 10 minutes
          if (totalSeconds <= environment.qrCountdownSec) {
            this.showTimer = true;
            this.onShowExpiryDate.emit(true);
          }

          // Red background if <= 1 minute
          this.isAlert = totalSeconds <= environment.qrCountdownAlertSec;
        } else {
          clearInterval(this.interval);
          this.minutes = '00';
          this.seconds = '00';
          this.onTimeup.emit(true);
        }
      }, 1000);
    }
  }

  ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}
